package ch.wsl.typings.jspreadsheetCe.mod

import ch.wsl.typings.jspreadsheetCe.anon.Data
import ch.wsl.typings.jspreadsheetCe.jspreadsheetCeInts.`0`
import ch.wsl.typings.jspreadsheetCe.jspreadsheetCeInts.`1`
import ch.wsl.typings.std.Record
import org.scalajs.dom.HTMLElement
import org.scalajs.dom.HTMLTableCellElement
import org.scalajs.dom.PointerEvent
import org.scalablytyped.runtime.StObject
import scala.scalajs.js
import scala.scalajs.js.`|`
import scala.scalajs.js.annotation.{JSGlobalScope, JSGlobal, JSImport, JSName, JSBracketAccess}

@js.native
trait JSpreadsheetOptions extends StObject {
  
  /**
    * Show or not the "about" item in the context menu.
    * @default true
    */
  var about: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow comments over the cells.
    * @default false
    */
  var allowComments: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow delete a column.
    * @default true
    */
  var allowDeleteColumn: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow delete a row.
    * @default true
    */
  var allowDeleteRow: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow remove all rows. Otherwise, at least one row will be kept.
    * @default false
    */
  var allowDeletingAllRows: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow table export as csv.
    * @default true
    */
  var allowExport: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow insert a new column.
    * @default true
    */
  var allowInsertColumn: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow insert a new row.
    * @default true
    */
  var allowInsertRow: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow user to insert a new column using tab key on last column.
    * @default true
    */
  var allowManualInsertColumn: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow user to insert a new row using space key on last row.
    * @default true
    */
  var allowManualInsertRow: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow rename a column.
    * @default true
    */
  var allowRenameColumn: js.UndefOr[Boolean] = js.native
  
  /**
    * If true, Jss will try to convert cell contents used in formulas to numbers
    * @default true
    */
  var autoCasting: js.UndefOr[Boolean] = js.native
  
  /**
    * Auto increment actions when using the dragging corner.
    * @default true
    */
  var autoIncrement: js.UndefOr[Boolean] = js.native
  
  /**
    * Css classes to apply to cells. Only one class per cell is accepted.
    * @example
    * {
    *    A1: "some-class",
    *    B3: "another-class"
    * }
    */
  var classes: js.UndefOr[Record[String, String]] = js.native
  
  /**
    * Column alignments. But they will only be used if the alignment is not specified in the column itself.
    * @deprecated Column alignment should be specified in the {@link JSpreadsheetOptions.columns} property.
    */
  var colAlignments: js.UndefOr[js.Array[js.UndefOr[HorizontalAlign | Null]]] = js.native
  
  /**
    * Column titles. But they will only be used if the title is not specified in the column itself.
    * @deprecated Column titles should be specified in the {@link JSpreadsheetOptions.columns} property.
    */
  var colHeaders: js.UndefOr[js.Array[js.UndefOr[String | Null]]] = js.native
  
  /**
    * Column widths. But they will only be used if the width is not specified in the column itself.
    * @deprecated Column widths should be specified in the {@link JSpreadsheetOptions.columns} property.
    */
  var colWidths: js.UndefOr[js.Array[js.UndefOr[String | Double | Null]]] = js.native
  
  /**
    * Allow column dragging.
    * @default false
    */
  var columnDrag: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow column resizing.
    * @default true
    */
  var columnResize: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow column sorting.
    * @default true
    */
  var columnSorting: js.UndefOr[Boolean] = js.native
  
  /** Column settings. */
  var columns: js.UndefOr[js.Array[Column]] = js.native
  
  /** Context menu content. */
  var contextMenu: js.UndefOr[
    js.Function4[
      /* instance */ JspreadsheetInstance, 
      /* colIndex */ String | Null, 
      /* rowIndex */ String | Null, 
      /* event */ PointerEvent, 
      js.Array[js.Object]
    ]
  ] = js.native
  
  /**
    * If true, copy and export will bring formula results. If false, it will bring formulas.
    * @default false
    */
  var copyCompatibility: js.UndefOr[Boolean] = js.native
  
  /**
    * Load a external CSV file from this URL.
    */
  var csv: js.UndefOr[String] = js.native
  
  /**
    * Default delimiter for the CSV file. This value is used for both import and export.
    * @default ","
    */
  var csvDelimiter: js.UndefOr[String] = js.native
  
  /**
    * Default filename for a download method.
    * @default "jspreadsheet"
    */
  var csvFileName: js.UndefOr[String] = js.native
  
  /**
    * Load header titles from the CSV file.
    * @default true
    */
  var csvHeaders: js.UndefOr[Boolean] = js.native
  
  /** Data loaded into the spreadsheet. */
  var data: js.UndefOr[js.Array[js.Array[CellValue] | (Record[String, CellValue])]] = js.native
  
  /**
    * Default horizontal alignment used when a column does not have its alignment specified.
    * @default "center"
    */
  var defaultColAlign: js.UndefOr[HorizontalAlign] = js.native
  
  /**
    * Default width for columns with no specified width.
    * @default 50
    */
  var defaultColWidth: js.UndefOr[Double] = js.native
  
  /**
    * Default row height.
    */
  var defaultRowHeight: js.UndefOr[Double] = js.native
  
  /**
    * Detach the HTML table when calling updateTable.
    * @default false
    */
  var detachForUpdates: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow table edition.
    * @default true
    */
  var editable: js.UndefOr[Boolean] = js.native
  
  /**
    * Enable column filters.
    * @default false
    */
  var filters: js.UndefOr[Boolean] = js.native
  
  /**
    * Set the initial footer of the spreadsheet
    */
  var footers: js.UndefOr[js.Array[js.Array[String]]] = js.native
  
  /**
    * Number of columns frozen at the top of the spreadsheet.
    */
  var freezeColumns: js.UndefOr[Double] = js.native
  
  /**
    * Fullscreen mode.
    * @default false
    */
  var fullscreen: js.UndefOr[Boolean] = js.native
  
  var imageOptions: js.UndefOr[Any] = js.native
  
  /**
    * Include header titles when all cell contents are copied.
    * @default false
    */
  var includeHeadersOnCopy: js.UndefOr[Boolean] = js.native
  
  /**
    * Include header titles on download.
    * @default false
    */
  var includeHeadersOnDownload: js.UndefOr[Boolean] = js.native
  
  /** Activate the table lazyloading. */
  var lazyLoading: js.UndefOr[Boolean] = js.native
  
  /**
    * Show a loading spin while populating the table.
    * @default false
    */
  var loadingSpin: js.UndefOr[Boolean] = js.native
  
  /** Cells to be merged in the table innitialization. */
  var mergeCells: js.UndefOr[Record[String, js.Tuple2[Double, Double]]] = js.native
  
  /**
    * Meta information.
    */
  var meta: js.UndefOr[Record[String, MetaInformation]] = js.native
  
  /**
    * HTTP method used to load external data.
    * @default "GET"
    */
  var method: js.UndefOr[String] = js.native
  
  /**
    * Minimum table dimensions: [cols, rows].
    * @default [0, 0]
    */
  var minDimensions: js.UndefOr[js.Tuple2[Double, Double]] = js.native
  
  /**
    * Minimum number of spare cols.
    * @default 0
    */
  var minSpareCols: js.UndefOr[Double] = js.native
  
  /**
    * Minimum number of spare rows.
    * @default 0
    */
  var minSpareRows: js.UndefOr[Double] = js.native
  
  /** Define the nested headers. */
  var nestedHeaders: js.UndefOr[js.Array[js.Array[NestedHeaderCell] | NestedHeaderCell]] = js.native
  
  /**
    * Occurs after all changes are applied in the tables.
    * @param element - Root HTML element of this jss instance.
    * @param changes - list of changes.
    */
  var onafterchanges: js.UndefOr[
    js.Function2[/* element */ JspreadsheetInstanceElement, /* changes */ js.Array[CellChange], Unit]
  ] = js.native
  
  /**
    * Occurs before a column value is changed. If any value is returned, it will be the cell's new value.
    * @param element - Root HTML element of this jss instance.
    * @param cell - HTML element that represents the cell being changed.
    * @param colIndex - Cell column index being changed.
    * @param rowIndex - Cell row index being changed.
    * @param newValue - Value being applied to the cell
    */
  var onbeforechange: js.UndefOr[
    js.Function5[
      /* element */ JspreadsheetInstanceElement, 
      /* cell */ HTMLTableCellElement, 
      /* colIndex */ String | Double, 
      /* rowIndex */ String | Double, 
      /* newValue */ CellValue, 
      js.UndefOr[CellValue]
    ]
  ] = js.native
  
  /**
    * Occurs before a column is excluded. If this method returns false, the removal will be canceled.
    * @param element - Root HTML element of this jss instance.
    * @param colIndex - Column index from which the removal will start.
    * @param numOfColumns - Number of columns to be removed.
    */
  var onbeforedeletecolumn: js.UndefOr[
    js.Function3[
      /* element */ JspreadsheetInstanceElement, 
      /* colIndex */ Double, 
      /* numOfColumns */ Double, 
      js.UndefOr[Boolean]
    ]
  ] = js.native
  
  /**
    * Occurs before a row is deleted. If this method returns false, the removal will be canceled.
    * @param element - Root HTML element of this jss instance.
    * @param rowIndex - Position of the row where removal will begin.
    * @param numOfRows - Number of rows that will be removed.
    */
  var onbeforedeleterow: js.UndefOr[
    js.Function3[
      /* element */ JspreadsheetInstanceElement, 
      /* rowIndex */ Double, 
      /* numOfRows */ Double, 
      js.UndefOr[Boolean]
    ]
  ] = js.native
  
  /**
    * Occurs before a new column is inserted. If this method returns false, the insertion will be canceled.
    * @param element - Root HTML element of this jss instance.
    * @param colIndex - Position of the column used as a reference for the insertion.
    * @param numOfColumns - Number of columns that should be added.
    * @param insertBefore - The columns should be inserted before or after the informed position in the "colIndex" parameter.
    */
  var onbeforeinsertcolumn: js.UndefOr[
    js.Function4[
      /* element */ JspreadsheetInstanceElement, 
      /* colIndex */ Double, 
      /* numOfColumns */ Double, 
      /* insertBefore */ Boolean, 
      js.UndefOr[Boolean]
    ]
  ] = js.native
  
  /**
    * Occurs before a new row is inserted. If this method returns false, the insertion will be canceled.
    * @param element - Root HTML element of this jss instance.
    * @param rowIndex - Position of the row used as a reference for the insertion.
    * @param numOfRows - Number of rows that should be added.
    * @param insertBefore - The rows should be inserted before or after the informed position in the "rowindex" parameter.
    */
  var onbeforeinsertrow: js.UndefOr[
    js.Function4[
      /* element */ JspreadsheetInstanceElement, 
      /* rowIndex */ Double, 
      /* numOfRows */ Double, 
      /* insertBefore */ Boolean, 
      js.UndefOr[Boolean]
    ]
  ] = js.native
  
  /**
    * Occurs before the paste action is performed.
    *
    * If it returns false, the jss cancels the paste.
    * If it returns a string, it will be the content pasted into the worksheet.
    *
    * @param element - Root HTML element of this jss instance.
    * @param copiedText - Text being pasted to the spreadsheet.
    * @param colIndex - Column index where it will start the paste.
    * @param rowIndex - Row index where it will start the paste.
    */
  var onbeforepaste: js.UndefOr[
    js.Function4[
      /* element */ JspreadsheetInstanceElement, 
      /* copiedText */ String, 
      /* colIndex */ Double | String, 
      /* rowIndex */ Double | String, 
      js.UndefOr[Boolean | String]
    ]
  ] = js.native
  
  /**
    * Occurs before persisting any changes to the server.
    *
    * This event is only called when the spreadsheet has the {@link JSpreadsheetOptions.persistance} property set.
    *
    * If this event returns false, the change is not persisted on the server.
    * If it returns a truthy value, that value is persisted instead of the initial value.
    * @param element - Root HTML element of this jss instance.
    * @param jssInstance - Jss Instance.
    * @param data - Changed data.
    */
  var onbeforesave: js.UndefOr[
    js.Function3[
      /* element */ JspreadsheetInstanceElement, 
      /* jssInstance */ JspreadsheetInstance, 
      /* data */ js.Array[Data], 
      Any
    ]
  ] = js.native
  
  /**
    * Occurs when the table is blurred.
    * @param element - Root HTML element of this jss instance.
    */
  var onblur: js.UndefOr[js.Function1[/* element */ JspreadsheetInstanceElement, Unit]] = js.native
  
  /**
    * Occurs after a column value is changed.
    * @param element - Root HTML element of this jss instance.
    * @param cell - HTML element that represents the cell being changed.
    * @param colIndex - Cell column index being changed.
    * @param rowIndex - Cell row index being changed.
    * @param newValue - New cell value.
    * @param oldValue - Old cell value.
    */
  var onchange: js.UndefOr[
    js.Function6[
      /* element */ JspreadsheetInstanceElement, 
      /* cell */ HTMLTableCellElement, 
      /* colIndex */ String | Double, 
      /* rowIndex */ String | Double, 
      /* newValue */ CellValue, 
      /* oldValue */ CellValue, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when a column heading is changed.
    * @param element - Root HTML element of this jss instance.
    * @param colIndex - Index of the column that was renamed.
    * @param oldValue - Old column title.
    * @param newValue - New column title.
    */
  var onchangeheader: js.UndefOr[
    js.Function4[
      /* element */ JspreadsheetInstanceElement, 
      /* colIndex */ String | Double, 
      /* oldValue */ String, 
      /* newValue */ String, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when a "setMeta" is called.
    *
    * This event can be called with two different sets of arguments, depending on how the "setMeta" method was called.
    * With three arguments, which will be called "simple mode", and with one argument, which will be called "complex mode".
    *
    * @param cellName - The name of the cell that underwent the change, if in simple mode, or an object with the metadata changes, if in complex mode.
    * @param property - The name of the changed property, if in simple mode, or undefined, if in complex mode.
    * @param value - The value of the changed property, if in simple mode, or undefined, if in complex mode.
    */
  var onchangemeta: js.UndefOr[
    js.Function3[
      /* cellName */ String | (Record[String, Record[String, Any]]), 
      /* property */ js.UndefOr[String], 
      /* value */ js.UndefOr[String], 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when the page is changed.
    * @param element - Root HTML element of this jss instance.
    * @param newPageNumber - Page the worksheet is on.
    * @param oldPageNumber - Page the worksheet was on.
    */
  var onchangepage: js.UndefOr[
    js.Function3[
      /* element */ JspreadsheetInstanceElement, 
      /* newPageNumber */ Double, 
      /* oldPageNumber */ Double, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when a "setStyle" is called.
    *
    * This event can be called with two different sets of arguments, depending on how the "setStyle" method was called.
    * With three arguments, which will be called "simple mode", and with one argument, which will be called "complex mode".
    *
    * @param cellName - The name of the cell that underwent the change, if in simple mode, or an object with the changes, if in complex mode.
    * @param cssProperty - The name of the changed property, if in simple mode, or undefined, if in complex mode.
    * @param propertyValue - The new value of the changed property, if in simple mode, or undefined, if in complex mode.
    */
  var onchangestyle: js.UndefOr[
    js.Function3[
      /* cellName */ String | (Record[String, String]), 
      /* cssProperty */ js.UndefOr[String], 
      /* propertyValue */ js.UndefOr[String], 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when a comment is changed.
    * @param element - Root HTML element of this jss instance.
    * @param newComment - New comment.
    * @param oldComment - Old comment.
    * @param cellPosition - Respectively the column and row indices of the cell whose comment was changed.
    * @param colIndex - Column index of the cell whose comment was changed.
    * @param rowIndex - Row index of the cell whose comment was changed.
    */
  var oncomments: js.UndefOr[
    js.Function6[
      /* element */ JspreadsheetInstanceElement, 
      /* newComment */ String | Null, 
      /* oldComment */ String | Null, 
      /* cellPosition */ js.Tuple2[Double, Double], 
      /* colIndex */ Double, 
      /* rowIndex */ Double, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when the contents of one or more cells are copied.
    * @param element - Root HTML element of this jss instance.
    * @param copiedData - Data that has been copied.
    * @param hash - Deprecated.
    */
  var oncopy: js.UndefOr[
    js.Function3[
      /* element */ JspreadsheetInstanceElement, 
      /* copiedData */ js.Array[String], 
      /* hash */ Double, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when an editor is opened.
    * @param element - Root HTML element of this jss instance.
    * @param td - Td tag of the cell whose editor was opened.
    * @param colIndex - Column index of the cell whose editor was opened.
    * @param rowIndex - Row index of the cell whose editor was opened.
    * @param input - Input of the editor that was opened.
    */
  var oncreateeditor: js.UndefOr[
    js.Function5[
      /* element */ JspreadsheetInstanceElement, 
      /* td */ HTMLTableCellElement, 
      /* colIndex */ String, 
      /* rowIndex */ String, 
      /* input */ HTMLElement, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs after a column is excluded.
    * @param element - Root HTML element of this jss instance.
    * @param colIndex - Column index from which removal began.
    * @param numOfColumns - Number of columns removed.
    * @param deletedCells - List of cells removed.
    */
  var ondeletecolumn: js.UndefOr[
    js.Function4[
      /* element */ JspreadsheetInstanceElement, 
      /* colIndex */ Double, 
      /* numOfColumns */ Double, 
      /* deletedCells */ js.Array[js.Array[HTMLTableCellElement]], 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs after a row is excluded.
    * @param element - Root HTML element of this jss instance.
    * @param rowIndex - Row index from which removal began.
    * @param numOfRows - Number of rows removed.
    * @param deletedCells - List of cells removed.
    */
  var ondeleterow: js.UndefOr[
    js.Function4[
      /* element */ JspreadsheetInstanceElement, 
      /* rowIndex */ Double, 
      /* numOfRows */ Double, 
      /* deletedCells */ js.Array[js.Array[HTMLTableCellElement]], 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when a closeEditor is called.
    * @param element - Root HTML element of this jss instance.
    * @param td - Td tag of the cell whose editor was opened.
    * @param colIndex - Column index of the cell whose editor was opened.
    * @param rowIndex - Row index of the cell whose editor was opened.
    * @param editorValue - Value that was in the editor.
    * @param wasSaved - Whether the value which was in the editor was saved in the cell or not.
    */
  var oneditionend: js.UndefOr[
    js.Function6[
      /* element */ JspreadsheetInstanceElement, 
      /* td */ HTMLTableCellElement, 
      /* colIndex */ Double, 
      /* rowIndex */ Double, 
      /* editorValue */ CellValue, 
      /* wasSaved */ Boolean, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when a openEditor is called.
    * @param element - Root HTML element of this jss instance.
    * @param td - Td tag of the cell whose editor was opened.
    * @param colIndex - Column index of the cell whose editor was opened.
    * @param rowIndex - Row index of the cell whose editor was opened.
    */
  var oneditionstart: js.UndefOr[
    js.Function4[
      /* element */ JspreadsheetInstanceElement, 
      /* td */ HTMLTableCellElement, 
      /* colIndex */ String, 
      /* rowIndex */ String, 
      Unit
    ]
  ] = js.native
  
  /**
    * Event fired when any other event fires. It runs before the event that was called.
    *
    * If the called event has not been defined, the jss considers the value returned by onevent as the value returned by the called event.
    * @param event - Name of the event that was called.
    * @param rest - Arguments of the event that was called.
    */
  var onevent: js.UndefOr[js.Function2[/* event */ String, /* repeated */ Any, Any]] = js.native
  
  /**
    * Occurs when the table is focused.
    * @param element - Root HTML element of this jss instance.
    */
  var onfocus: js.UndefOr[js.Function1[/* element */ JspreadsheetInstanceElement, Unit]] = js.native
  
  /**
    * Occurs after a new column is inserted.
    * @param element - Root HTML element of this jss instance.
    * @param colIndex - Position of the column used as a reference for the insertion.
    * @param numOfColumns - Number of columns that were added.
    * @param addedCells - List of HTML elements that represent the added cells.
    * @param insertBefore - Columns were inserted before or after the informed position in the "colIndex" parameter.
    */
  var oninsertcolumn: js.UndefOr[
    js.Function5[
      /* element */ JspreadsheetInstanceElement, 
      /* colIndex */ Double, 
      /* numOfColumns */ Double, 
      /* addedCells */ js.Array[js.Array[HTMLTableCellElement]], 
      /* insertBefore */ js.UndefOr[Boolean], 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs after a new row is inserted.
    * @param element - Root HTML element of this jss instance.
    * @param rowIndex - Position of the row used as a reference for the insertion.
    * @param numOfRows - Number of rows that were added.
    * @param addedCells - List of HTML elements that represent the added cells.
    * @param insertBefore - Rows were inserted before or after the informed position in the "rowIndex" parameter.
    */
  var oninsertrow: js.UndefOr[
    js.Function5[
      /* element */ JspreadsheetInstanceElement, 
      /* rowIndex */ Double, 
      /* numOfRows */ Double, 
      /* addedCells */ js.Array[js.Array[HTMLTableCellElement]], 
      /* insertBefore */ Boolean, 
      Unit
    ]
  ] = js.native
  
  /**
    * This method is called when the method setData.
    * @param element - Root HTML element of this jss instance.
    * @param instance - Jspreadsheet instance.
    */
  var onload: js.UndefOr[
    js.Function2[/* element */ JspreadsheetInstanceElement, /* instance */ JspreadsheetInstance, Unit]
  ] = js.native
  
  /**
    * Occurs when a group of cells is merged.
    * @param element - Root HTML element of this jss instance.
    * @param mergeInitialCell - Name of the cell that is the top left corner of the merge.
    * @param colspan - Number of columns this merge occupies.
    * @param rowspan - Number of rows this merge occupies.
    */
  var onmerge: js.UndefOr[
    js.Function4[
      /* element */ JspreadsheetInstanceElement, 
      /* mergeInitialCell */ String, 
      /* colspan */ Double, 
      /* rowspan */ Double, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs after a column is moved to a new position.
    * @param element - Root HTML element of this jss instance.
    * @param oldPosition - Column index before movement.
    * @param newPosition - Column index after movement.
    */
  var onmovecolumn: js.UndefOr[
    js.Function3[
      /* element */ JspreadsheetInstanceElement, 
      /* oldPosition */ Double, 
      /* newPosition */ Double, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs after a row is moved to a new position.
    * @param element - Root HTML element of this jss instance.
    * @param oldPosition - Row index before movement.
    * @param newPosition - Row index after movement.
    */
  var onmoverow: js.UndefOr[
    js.Function3[
      /* element */ JspreadsheetInstanceElement, 
      /* oldPosition */ String | Double, 
      /* newPosition */ Double, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs after a paste action is performed in the javascript table.
    * @param element - Root HTML element of this jss instance.
    * @param pastedData - Data pasted to the spreadsheet.
    */
  var onpaste: js.UndefOr[
    js.Function2[
      /* element */ JspreadsheetInstanceElement, 
      /* pastedData */ js.Array[js.Array[String]], 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when a change is redone.
    * @param element - Root HTML element of this jss instance.
    * @param historyRecord - History item that was redone. If there are no more actions to redo, it takes the value undefined.
    */
  var onredo: js.UndefOr[
    js.Function2[
      /* element */ JspreadsheetInstanceElement, 
      /* historyRecord */ js.UndefOr[HistoryRecord], 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs after a change in column width.
    * @param element - Root HTML element of this jss instance.
    * @param colIndex - Index of column being resized.
    * @param newWidth - New column width.
    * @param oldWidth - Old column width.
    */
  var onresizecolumn: js.UndefOr[
    js.Function4[
      /* element */ JspreadsheetInstanceElement, 
      /* colIndex */ String | Double, 
      /* newWidth */ String | Double, 
      /* oldWidth */ Double, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs after a change in row height.
    * @param element - Root HTML element of this jss instance.
    * @param rowIndex - Index of row being resized.
    * @param newHeight - New row height.
    * @param oldHeight - Old row height.
    */
  var onresizerow: js.UndefOr[
    js.Function4[
      /* element */ JspreadsheetInstanceElement, 
      /* rowIndex */ String | Double, 
      /* newHeight */ Double, 
      /* oldHeight */ Double, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when persistence on the server succeeds.
    * @param element - Root HTML element of this jss instance.
    * @param jssInstance - Jss Instance.
    * @param data - Data that has been sent to the server.
    */
  var onsave: js.UndefOr[
    js.Function3[
      /* element */ JspreadsheetInstanceElement, 
      /* jssInstance */ JspreadsheetInstance, 
      /* data */ Any, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when selection is changed.
    * @param element - Root HTML element of this jss instance.
    * @param borderLeftIndex - Index of the first column contained by the selection.
    * @param borderTopIndex - Index of the first row contained by the selection.
    * @param borderRightIndex - Index of the last column contained by the selection.
    * @param borderBottomIndex - Index of the last row contained by the selection.
    */
  var onselection: js.UndefOr[
    js.Function6[
      /* element */ JspreadsheetInstanceElement, 
      /* borderLeftIndex */ Double, 
      /* borderTopIndex */ Double, 
      /* borderRightIndex */ Double, 
      /* borderBottomIndex */ Double, 
      /* origin */ Any, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs after a colum is sorted.
    * @param element - Root HTML element of this jss instance.
    * @param colIndex - Index of the column that was sorted.
    * @param order - Sorting direction. 0 for ascending and 1 for descending.
    */
  var onsort: js.UndefOr[
    js.Function3[
      /* element */ JspreadsheetInstanceElement, 
      /* colIndex */ String | Double, 
      /* order */ `0` | `1`, 
      Unit
    ]
  ] = js.native
  
  /**
    * Occurs when a change is undone.
    * @param element - Root HTML element of this jss instance.
    * @param historyRecord - History item that was undone. If there are no more actions to undo, it takes the value undefined.
    */
  var onundo: js.UndefOr[
    js.Function2[
      /* element */ JspreadsheetInstanceElement, 
      /* historyRecord */ js.UndefOr[HistoryRecord], 
      Unit
    ]
  ] = js.native
  
  /** Number of rows per page. */
  var pagination: js.UndefOr[Double] = js.native
  
  /**
    * Values available in the dropdown for choosing the number of rows per page.
    *
    * This dropdown is only visible when the {@link JSpreadsheetOptions.search} option is true and the {@link JSpreadsheetOptions.pagination} option is greater than 0.
    */
  var paginationOptions: js.UndefOr[js.Array[Double]] = js.native
  
  /**
    * Enable execution of formulas inside the table.
    * @default true
    */
  var parseFormulas: js.UndefOr[Boolean] = js.native
  
  /**
    * Try to identify the column type when the instance is created with a table tag.
    * @default false
    */
  var parseTableAutoCellType: js.UndefOr[Boolean] = js.native
  
  /**
    * If creating the instance with a table tag, if this tag has no header, transform the first line into the header.
    * @default false
    */
  var parseTableFirstRowAsHeader: js.UndefOr[Boolean] = js.native
  
  /**
    * Route where requests for data persistence will be sent. If true, the {@link JSpreadsheetOptions.url} property value will be used instead.
    */
  var persistance: js.UndefOr[Boolean | String] = js.native
  
  /**
    * Request body used to load external data.
    */
  var requestVariables: js.UndefOr[Any] = js.native
  
  /**
    * HTML element where the events of this instance will be listened to.
    *
    * Even if this property is specified, some events will still be listened to on the document object.
    * @default document
    */
  var root: js.UndefOr[HTMLElement] = js.native
  
  /**
    * Allow row dragging.
    * @default true
    */
  var rowDrag: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow row resizing.
    * @default false
    */
  var rowResize: js.UndefOr[Boolean] = js.native
  
  /** Row settings. */
  var rows: js.UndefOr[js.Array[Row] | (Record[Double, Row])] = js.native
  
  /**
    * Allow search in the table.
    * @default false
    */
  var search: js.UndefOr[Boolean] = js.native
  
  /**
    * If true, Jss will capitalize characters that are part of formulas. This does not apply to characters enclosed in double quotes, which represent text within formulas.
    * @default true
    */
  var secureFormulas: js.UndefOr[Boolean] = js.native
  
  /**
    * Allow copy using the selection corner.
    * @default true
    */
  var selectionCopy: js.UndefOr[Boolean] = js.native
  
  /**
    * Function used in sorting columns. If not specified, the default function will be used.
    * @param order - Sorting direction. 0 for ascending and 1 for descending.
    */
  var sorting: js.UndefOr[
    js.Function1[
      /* order */ `0` | `1`, 
      js.Function2[/* itemA */ SortingItem, /* itemB */ SortingItem, Double]
    ]
  ] = js.native
  
  /**
    * If true, HTML inside column headings or cell values will be treated as regular text.
    *
    * If false, the HTML will be treated as HTML.
    * @default true
    */
  var stripHTML: js.UndefOr[Boolean] = js.native
  
  /**
    * If true, the HTML present inside the cells is also copied when the user copies cells from the spreadsheet.
    *
    * This property only works when the {@link JSpreadsheetOptions.copyCompatibility} option is equal to "true".
    * @default false
    */
  var stripHTMLOnCopy: js.UndefOr[Boolean] = js.native
  
  /**
    * Cell styles.
    */
  var style: js.UndefOr[Record[String, String]] = js.native
  
  /**
    * Set the max height of the table.
    * This property is only used when {@link JSpreadsheetOptions.tableOverflow} is allowed.
    * @default "300px"
    */
  var tableHeight: js.UndefOr[String] = js.native
  
  /**
    * Allow table overflow.
    * @default false
    */
  var tableOverflow: js.UndefOr[Boolean] = js.native
  
  /**
    * Set the max width of the table.
    * This property is only used when {@link JSpreadsheetOptions.tableOverflow} is allowed.
    */
  var tableWidth: js.UndefOr[String] = js.native
  
  /** All messages to be customized. */
  var text: js.UndefOr[TranslationOptions] = js.native
  
  /**
    * If true, cell contents may overflow over empty cells.
    * @default false
    */
  var textOverflow: js.UndefOr[Boolean] = js.native
  
  /** Add custom toolbars. */
  var toolbar: js.UndefOr[js.Array[ToolbarItem]] = js.native
  
  /** Method to config custom script execution. NOTE: This does not work with lazyLoading, Pagination or Search options. */
  var updateTable: js.UndefOr[
    js.Function7[
      /* instance */ JspreadsheetInstance, 
      /* cell */ HTMLTableCellElement, 
      /* colIndex */ Double, 
      /* rowIndex */ Double, 
      /* value */ CellValue, 
      /* displayedValue */ String, 
      /* cellName */ String, 
      Unit
    ]
  ] = js.native
  
  /** Load a external json file from this URL. */
  var url: js.UndefOr[String] = js.native
  
  /**
    * Global text wrapping.
    * @default false
    */
  var wordWrap: js.UndefOr[Boolean] = js.native
}
object JSpreadsheetOptions {
  
  @scala.inline
  def apply(): JSpreadsheetOptions = {
    val __obj = js.Dynamic.literal()
    __obj.asInstanceOf[JSpreadsheetOptions]
  }
  
  @scala.inline
  implicit class MutableBuilder[Self <: JSpreadsheetOptions] (val x: Self) extends AnyVal {
    
    @scala.inline
    def setAbout(value: Boolean): Self = StObject.set(x, "about", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAboutUndefined: Self = StObject.set(x, "about", js.undefined)
    
    @scala.inline
    def setAllowComments(value: Boolean): Self = StObject.set(x, "allowComments", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAllowCommentsUndefined: Self = StObject.set(x, "allowComments", js.undefined)
    
    @scala.inline
    def setAllowDeleteColumn(value: Boolean): Self = StObject.set(x, "allowDeleteColumn", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAllowDeleteColumnUndefined: Self = StObject.set(x, "allowDeleteColumn", js.undefined)
    
    @scala.inline
    def setAllowDeleteRow(value: Boolean): Self = StObject.set(x, "allowDeleteRow", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAllowDeleteRowUndefined: Self = StObject.set(x, "allowDeleteRow", js.undefined)
    
    @scala.inline
    def setAllowDeletingAllRows(value: Boolean): Self = StObject.set(x, "allowDeletingAllRows", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAllowDeletingAllRowsUndefined: Self = StObject.set(x, "allowDeletingAllRows", js.undefined)
    
    @scala.inline
    def setAllowExport(value: Boolean): Self = StObject.set(x, "allowExport", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAllowExportUndefined: Self = StObject.set(x, "allowExport", js.undefined)
    
    @scala.inline
    def setAllowInsertColumn(value: Boolean): Self = StObject.set(x, "allowInsertColumn", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAllowInsertColumnUndefined: Self = StObject.set(x, "allowInsertColumn", js.undefined)
    
    @scala.inline
    def setAllowInsertRow(value: Boolean): Self = StObject.set(x, "allowInsertRow", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAllowInsertRowUndefined: Self = StObject.set(x, "allowInsertRow", js.undefined)
    
    @scala.inline
    def setAllowManualInsertColumn(value: Boolean): Self = StObject.set(x, "allowManualInsertColumn", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAllowManualInsertColumnUndefined: Self = StObject.set(x, "allowManualInsertColumn", js.undefined)
    
    @scala.inline
    def setAllowManualInsertRow(value: Boolean): Self = StObject.set(x, "allowManualInsertRow", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAllowManualInsertRowUndefined: Self = StObject.set(x, "allowManualInsertRow", js.undefined)
    
    @scala.inline
    def setAllowRenameColumn(value: Boolean): Self = StObject.set(x, "allowRenameColumn", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAllowRenameColumnUndefined: Self = StObject.set(x, "allowRenameColumn", js.undefined)
    
    @scala.inline
    def setAutoCasting(value: Boolean): Self = StObject.set(x, "autoCasting", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAutoCastingUndefined: Self = StObject.set(x, "autoCasting", js.undefined)
    
    @scala.inline
    def setAutoIncrement(value: Boolean): Self = StObject.set(x, "autoIncrement", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setAutoIncrementUndefined: Self = StObject.set(x, "autoIncrement", js.undefined)
    
    @scala.inline
    def setClasses(value: Record[String, String]): Self = StObject.set(x, "classes", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setClassesUndefined: Self = StObject.set(x, "classes", js.undefined)
    
    @scala.inline
    def setColAlignments(value: js.Array[js.UndefOr[HorizontalAlign | Null]]): Self = StObject.set(x, "colAlignments", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setColAlignmentsUndefined: Self = StObject.set(x, "colAlignments", js.undefined)
    
    @scala.inline
    def setColAlignmentsVarargs(value: (js.UndefOr[HorizontalAlign | Null])*): Self = StObject.set(x, "colAlignments", js.Array(value :_*))
    
    @scala.inline
    def setColHeaders(value: js.Array[js.UndefOr[String | Null]]): Self = StObject.set(x, "colHeaders", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setColHeadersUndefined: Self = StObject.set(x, "colHeaders", js.undefined)
    
    @scala.inline
    def setColHeadersVarargs(value: (js.UndefOr[String | Null])*): Self = StObject.set(x, "colHeaders", js.Array(value :_*))
    
    @scala.inline
    def setColWidths(value: js.Array[js.UndefOr[String | Double | Null]]): Self = StObject.set(x, "colWidths", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setColWidthsUndefined: Self = StObject.set(x, "colWidths", js.undefined)
    
    @scala.inline
    def setColWidthsVarargs(value: (js.UndefOr[String | Double | Null])*): Self = StObject.set(x, "colWidths", js.Array(value :_*))
    
    @scala.inline
    def setColumnDrag(value: Boolean): Self = StObject.set(x, "columnDrag", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setColumnDragUndefined: Self = StObject.set(x, "columnDrag", js.undefined)
    
    @scala.inline
    def setColumnResize(value: Boolean): Self = StObject.set(x, "columnResize", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setColumnResizeUndefined: Self = StObject.set(x, "columnResize", js.undefined)
    
    @scala.inline
    def setColumnSorting(value: Boolean): Self = StObject.set(x, "columnSorting", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setColumnSortingUndefined: Self = StObject.set(x, "columnSorting", js.undefined)
    
    @scala.inline
    def setColumns(value: js.Array[Column]): Self = StObject.set(x, "columns", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setColumnsUndefined: Self = StObject.set(x, "columns", js.undefined)
    
    @scala.inline
    def setColumnsVarargs(value: Column*): Self = StObject.set(x, "columns", js.Array(value :_*))
    
    @scala.inline
    def setContextMenu(
      value: (/* instance */ JspreadsheetInstance, /* colIndex */ String | Null, /* rowIndex */ String | Null, /* event */ PointerEvent) => js.Array[js.Object]
    ): Self = StObject.set(x, "contextMenu", js.Any.fromFunction4(value))
    
    @scala.inline
    def setContextMenuUndefined: Self = StObject.set(x, "contextMenu", js.undefined)
    
    @scala.inline
    def setCopyCompatibility(value: Boolean): Self = StObject.set(x, "copyCompatibility", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setCopyCompatibilityUndefined: Self = StObject.set(x, "copyCompatibility", js.undefined)
    
    @scala.inline
    def setCsv(value: String): Self = StObject.set(x, "csv", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setCsvDelimiter(value: String): Self = StObject.set(x, "csvDelimiter", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setCsvDelimiterUndefined: Self = StObject.set(x, "csvDelimiter", js.undefined)
    
    @scala.inline
    def setCsvFileName(value: String): Self = StObject.set(x, "csvFileName", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setCsvFileNameUndefined: Self = StObject.set(x, "csvFileName", js.undefined)
    
    @scala.inline
    def setCsvHeaders(value: Boolean): Self = StObject.set(x, "csvHeaders", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setCsvHeadersUndefined: Self = StObject.set(x, "csvHeaders", js.undefined)
    
    @scala.inline
    def setCsvUndefined: Self = StObject.set(x, "csv", js.undefined)
    
    @scala.inline
    def setData(value: js.Array[js.Array[CellValue] | (Record[String, CellValue])]): Self = StObject.set(x, "data", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setDataUndefined: Self = StObject.set(x, "data", js.undefined)
    
    @scala.inline
    def setDataVarargs(value: (js.Array[CellValue] | (Record[String, CellValue]))*): Self = StObject.set(x, "data", js.Array(value :_*))
    
    @scala.inline
    def setDefaultColAlign(value: HorizontalAlign): Self = StObject.set(x, "defaultColAlign", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setDefaultColAlignUndefined: Self = StObject.set(x, "defaultColAlign", js.undefined)
    
    @scala.inline
    def setDefaultColWidth(value: Double): Self = StObject.set(x, "defaultColWidth", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setDefaultColWidthUndefined: Self = StObject.set(x, "defaultColWidth", js.undefined)
    
    @scala.inline
    def setDefaultRowHeight(value: Double): Self = StObject.set(x, "defaultRowHeight", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setDefaultRowHeightUndefined: Self = StObject.set(x, "defaultRowHeight", js.undefined)
    
    @scala.inline
    def setDetachForUpdates(value: Boolean): Self = StObject.set(x, "detachForUpdates", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setDetachForUpdatesUndefined: Self = StObject.set(x, "detachForUpdates", js.undefined)
    
    @scala.inline
    def setEditable(value: Boolean): Self = StObject.set(x, "editable", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setEditableUndefined: Self = StObject.set(x, "editable", js.undefined)
    
    @scala.inline
    def setFilters(value: Boolean): Self = StObject.set(x, "filters", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setFiltersUndefined: Self = StObject.set(x, "filters", js.undefined)
    
    @scala.inline
    def setFooters(value: js.Array[js.Array[String]]): Self = StObject.set(x, "footers", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setFootersUndefined: Self = StObject.set(x, "footers", js.undefined)
    
    @scala.inline
    def setFootersVarargs(value: js.Array[String]*): Self = StObject.set(x, "footers", js.Array(value :_*))
    
    @scala.inline
    def setFreezeColumns(value: Double): Self = StObject.set(x, "freezeColumns", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setFreezeColumnsUndefined: Self = StObject.set(x, "freezeColumns", js.undefined)
    
    @scala.inline
    def setFullscreen(value: Boolean): Self = StObject.set(x, "fullscreen", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setFullscreenUndefined: Self = StObject.set(x, "fullscreen", js.undefined)
    
    @scala.inline
    def setImageOptions(value: Any): Self = StObject.set(x, "imageOptions", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setImageOptionsUndefined: Self = StObject.set(x, "imageOptions", js.undefined)
    
    @scala.inline
    def setIncludeHeadersOnCopy(value: Boolean): Self = StObject.set(x, "includeHeadersOnCopy", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setIncludeHeadersOnCopyUndefined: Self = StObject.set(x, "includeHeadersOnCopy", js.undefined)
    
    @scala.inline
    def setIncludeHeadersOnDownload(value: Boolean): Self = StObject.set(x, "includeHeadersOnDownload", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setIncludeHeadersOnDownloadUndefined: Self = StObject.set(x, "includeHeadersOnDownload", js.undefined)
    
    @scala.inline
    def setLazyLoading(value: Boolean): Self = StObject.set(x, "lazyLoading", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setLazyLoadingUndefined: Self = StObject.set(x, "lazyLoading", js.undefined)
    
    @scala.inline
    def setLoadingSpin(value: Boolean): Self = StObject.set(x, "loadingSpin", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setLoadingSpinUndefined: Self = StObject.set(x, "loadingSpin", js.undefined)
    
    @scala.inline
    def setMergeCells(value: Record[String, js.Tuple2[Double, Double]]): Self = StObject.set(x, "mergeCells", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setMergeCellsUndefined: Self = StObject.set(x, "mergeCells", js.undefined)
    
    @scala.inline
    def setMeta(value: Record[String, MetaInformation]): Self = StObject.set(x, "meta", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setMetaUndefined: Self = StObject.set(x, "meta", js.undefined)
    
    @scala.inline
    def setMethod(value: String): Self = StObject.set(x, "method", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setMethodUndefined: Self = StObject.set(x, "method", js.undefined)
    
    @scala.inline
    def setMinDimensions(value: js.Tuple2[Double, Double]): Self = StObject.set(x, "minDimensions", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setMinDimensionsUndefined: Self = StObject.set(x, "minDimensions", js.undefined)
    
    @scala.inline
    def setMinSpareCols(value: Double): Self = StObject.set(x, "minSpareCols", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setMinSpareColsUndefined: Self = StObject.set(x, "minSpareCols", js.undefined)
    
    @scala.inline
    def setMinSpareRows(value: Double): Self = StObject.set(x, "minSpareRows", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setMinSpareRowsUndefined: Self = StObject.set(x, "minSpareRows", js.undefined)
    
    @scala.inline
    def setNestedHeaders(value: js.Array[js.Array[NestedHeaderCell] | NestedHeaderCell]): Self = StObject.set(x, "nestedHeaders", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setNestedHeadersUndefined: Self = StObject.set(x, "nestedHeaders", js.undefined)
    
    @scala.inline
    def setNestedHeadersVarargs(value: (js.Array[NestedHeaderCell] | NestedHeaderCell)*): Self = StObject.set(x, "nestedHeaders", js.Array(value :_*))
    
    @scala.inline
    def setOnafterchanges(value: (/* element */ JspreadsheetInstanceElement, /* changes */ js.Array[CellChange]) => Unit): Self = StObject.set(x, "onafterchanges", js.Any.fromFunction2(value))
    
    @scala.inline
    def setOnafterchangesUndefined: Self = StObject.set(x, "onafterchanges", js.undefined)
    
    @scala.inline
    def setOnbeforechange(
      value: (/* element */ JspreadsheetInstanceElement, /* cell */ HTMLTableCellElement, /* colIndex */ String | Double, /* rowIndex */ String | Double, /* newValue */ CellValue) => js.UndefOr[CellValue]
    ): Self = StObject.set(x, "onbeforechange", js.Any.fromFunction5(value))
    
    @scala.inline
    def setOnbeforechangeUndefined: Self = StObject.set(x, "onbeforechange", js.undefined)
    
    @scala.inline
    def setOnbeforedeletecolumn(
      value: (/* element */ JspreadsheetInstanceElement, /* colIndex */ Double, /* numOfColumns */ Double) => js.UndefOr[Boolean]
    ): Self = StObject.set(x, "onbeforedeletecolumn", js.Any.fromFunction3(value))
    
    @scala.inline
    def setOnbeforedeletecolumnUndefined: Self = StObject.set(x, "onbeforedeletecolumn", js.undefined)
    
    @scala.inline
    def setOnbeforedeleterow(
      value: (/* element */ JspreadsheetInstanceElement, /* rowIndex */ Double, /* numOfRows */ Double) => js.UndefOr[Boolean]
    ): Self = StObject.set(x, "onbeforedeleterow", js.Any.fromFunction3(value))
    
    @scala.inline
    def setOnbeforedeleterowUndefined: Self = StObject.set(x, "onbeforedeleterow", js.undefined)
    
    @scala.inline
    def setOnbeforeinsertcolumn(
      value: (/* element */ JspreadsheetInstanceElement, /* colIndex */ Double, /* numOfColumns */ Double, /* insertBefore */ Boolean) => js.UndefOr[Boolean]
    ): Self = StObject.set(x, "onbeforeinsertcolumn", js.Any.fromFunction4(value))
    
    @scala.inline
    def setOnbeforeinsertcolumnUndefined: Self = StObject.set(x, "onbeforeinsertcolumn", js.undefined)
    
    @scala.inline
    def setOnbeforeinsertrow(
      value: (/* element */ JspreadsheetInstanceElement, /* rowIndex */ Double, /* numOfRows */ Double, /* insertBefore */ Boolean) => js.UndefOr[Boolean]
    ): Self = StObject.set(x, "onbeforeinsertrow", js.Any.fromFunction4(value))
    
    @scala.inline
    def setOnbeforeinsertrowUndefined: Self = StObject.set(x, "onbeforeinsertrow", js.undefined)
    
    @scala.inline
    def setOnbeforepaste(
      value: (/* element */ JspreadsheetInstanceElement, /* copiedText */ String, /* colIndex */ Double | String, /* rowIndex */ Double | String) => js.UndefOr[Boolean | String]
    ): Self = StObject.set(x, "onbeforepaste", js.Any.fromFunction4(value))
    
    @scala.inline
    def setOnbeforepasteUndefined: Self = StObject.set(x, "onbeforepaste", js.undefined)
    
    @scala.inline
    def setOnbeforesave(
      value: (/* element */ JspreadsheetInstanceElement, /* jssInstance */ JspreadsheetInstance, /* data */ js.Array[Data]) => Any
    ): Self = StObject.set(x, "onbeforesave", js.Any.fromFunction3(value))
    
    @scala.inline
    def setOnbeforesaveUndefined: Self = StObject.set(x, "onbeforesave", js.undefined)
    
    @scala.inline
    def setOnblur(value: /* element */ JspreadsheetInstanceElement => Unit): Self = StObject.set(x, "onblur", js.Any.fromFunction1(value))
    
    @scala.inline
    def setOnblurUndefined: Self = StObject.set(x, "onblur", js.undefined)
    
    @scala.inline
    def setOnchange(
      value: (/* element */ JspreadsheetInstanceElement, /* cell */ HTMLTableCellElement, /* colIndex */ String | Double, /* rowIndex */ String | Double, /* newValue */ CellValue, /* oldValue */ CellValue) => Unit
    ): Self = StObject.set(x, "onchange", js.Any.fromFunction6(value))
    
    @scala.inline
    def setOnchangeUndefined: Self = StObject.set(x, "onchange", js.undefined)
    
    @scala.inline
    def setOnchangeheader(
      value: (/* element */ JspreadsheetInstanceElement, /* colIndex */ String | Double, /* oldValue */ String, /* newValue */ String) => Unit
    ): Self = StObject.set(x, "onchangeheader", js.Any.fromFunction4(value))
    
    @scala.inline
    def setOnchangeheaderUndefined: Self = StObject.set(x, "onchangeheader", js.undefined)
    
    @scala.inline
    def setOnchangemeta(
      value: (/* cellName */ String | (Record[String, Record[String, Any]]), /* property */ js.UndefOr[String], /* value */ js.UndefOr[String]) => Unit
    ): Self = StObject.set(x, "onchangemeta", js.Any.fromFunction3(value))
    
    @scala.inline
    def setOnchangemetaUndefined: Self = StObject.set(x, "onchangemeta", js.undefined)
    
    @scala.inline
    def setOnchangepage(
      value: (/* element */ JspreadsheetInstanceElement, /* newPageNumber */ Double, /* oldPageNumber */ Double) => Unit
    ): Self = StObject.set(x, "onchangepage", js.Any.fromFunction3(value))
    
    @scala.inline
    def setOnchangepageUndefined: Self = StObject.set(x, "onchangepage", js.undefined)
    
    @scala.inline
    def setOnchangestyle(
      value: (/* cellName */ String | (Record[String, String]), /* cssProperty */ js.UndefOr[String], /* propertyValue */ js.UndefOr[String]) => Unit
    ): Self = StObject.set(x, "onchangestyle", js.Any.fromFunction3(value))
    
    @scala.inline
    def setOnchangestyleUndefined: Self = StObject.set(x, "onchangestyle", js.undefined)
    
    @scala.inline
    def setOncomments(
      value: (/* element */ JspreadsheetInstanceElement, /* newComment */ String | Null, /* oldComment */ String | Null, /* cellPosition */ js.Tuple2[Double, Double], /* colIndex */ Double, /* rowIndex */ Double) => Unit
    ): Self = StObject.set(x, "oncomments", js.Any.fromFunction6(value))
    
    @scala.inline
    def setOncommentsUndefined: Self = StObject.set(x, "oncomments", js.undefined)
    
    @scala.inline
    def setOncopy(
      value: (/* element */ JspreadsheetInstanceElement, /* copiedData */ js.Array[String], /* hash */ Double) => Unit
    ): Self = StObject.set(x, "oncopy", js.Any.fromFunction3(value))
    
    @scala.inline
    def setOncopyUndefined: Self = StObject.set(x, "oncopy", js.undefined)
    
    @scala.inline
    def setOncreateeditor(
      value: (/* element */ JspreadsheetInstanceElement, /* td */ HTMLTableCellElement, /* colIndex */ String, /* rowIndex */ String, /* input */ HTMLElement) => Unit
    ): Self = StObject.set(x, "oncreateeditor", js.Any.fromFunction5(value))
    
    @scala.inline
    def setOncreateeditorUndefined: Self = StObject.set(x, "oncreateeditor", js.undefined)
    
    @scala.inline
    def setOndeletecolumn(
      value: (/* element */ JspreadsheetInstanceElement, /* colIndex */ Double, /* numOfColumns */ Double, /* deletedCells */ js.Array[js.Array[HTMLTableCellElement]]) => Unit
    ): Self = StObject.set(x, "ondeletecolumn", js.Any.fromFunction4(value))
    
    @scala.inline
    def setOndeletecolumnUndefined: Self = StObject.set(x, "ondeletecolumn", js.undefined)
    
    @scala.inline
    def setOndeleterow(
      value: (/* element */ JspreadsheetInstanceElement, /* rowIndex */ Double, /* numOfRows */ Double, /* deletedCells */ js.Array[js.Array[HTMLTableCellElement]]) => Unit
    ): Self = StObject.set(x, "ondeleterow", js.Any.fromFunction4(value))
    
    @scala.inline
    def setOndeleterowUndefined: Self = StObject.set(x, "ondeleterow", js.undefined)
    
    @scala.inline
    def setOneditionend(
      value: (/* element */ JspreadsheetInstanceElement, /* td */ HTMLTableCellElement, /* colIndex */ Double, /* rowIndex */ Double, /* editorValue */ CellValue, /* wasSaved */ Boolean) => Unit
    ): Self = StObject.set(x, "oneditionend", js.Any.fromFunction6(value))
    
    @scala.inline
    def setOneditionendUndefined: Self = StObject.set(x, "oneditionend", js.undefined)
    
    @scala.inline
    def setOneditionstart(
      value: (/* element */ JspreadsheetInstanceElement, /* td */ HTMLTableCellElement, /* colIndex */ String, /* rowIndex */ String) => Unit
    ): Self = StObject.set(x, "oneditionstart", js.Any.fromFunction4(value))
    
    @scala.inline
    def setOneditionstartUndefined: Self = StObject.set(x, "oneditionstart", js.undefined)
    
    @scala.inline
    def setOnevent(value: (/* event */ String, /* repeated */ Any) => Any): Self = StObject.set(x, "onevent", js.Any.fromFunction2(value))
    
    @scala.inline
    def setOneventUndefined: Self = StObject.set(x, "onevent", js.undefined)
    
    @scala.inline
    def setOnfocus(value: /* element */ JspreadsheetInstanceElement => Unit): Self = StObject.set(x, "onfocus", js.Any.fromFunction1(value))
    
    @scala.inline
    def setOnfocusUndefined: Self = StObject.set(x, "onfocus", js.undefined)
    
    @scala.inline
    def setOninsertcolumn(
      value: (/* element */ JspreadsheetInstanceElement, /* colIndex */ Double, /* numOfColumns */ Double, /* addedCells */ js.Array[js.Array[HTMLTableCellElement]], /* insertBefore */ js.UndefOr[Boolean]) => Unit
    ): Self = StObject.set(x, "oninsertcolumn", js.Any.fromFunction5(value))
    
    @scala.inline
    def setOninsertcolumnUndefined: Self = StObject.set(x, "oninsertcolumn", js.undefined)
    
    @scala.inline
    def setOninsertrow(
      value: (/* element */ JspreadsheetInstanceElement, /* rowIndex */ Double, /* numOfRows */ Double, /* addedCells */ js.Array[js.Array[HTMLTableCellElement]], /* insertBefore */ Boolean) => Unit
    ): Self = StObject.set(x, "oninsertrow", js.Any.fromFunction5(value))
    
    @scala.inline
    def setOninsertrowUndefined: Self = StObject.set(x, "oninsertrow", js.undefined)
    
    @scala.inline
    def setOnload(value: (/* element */ JspreadsheetInstanceElement, /* instance */ JspreadsheetInstance) => Unit): Self = StObject.set(x, "onload", js.Any.fromFunction2(value))
    
    @scala.inline
    def setOnloadUndefined: Self = StObject.set(x, "onload", js.undefined)
    
    @scala.inline
    def setOnmerge(
      value: (/* element */ JspreadsheetInstanceElement, /* mergeInitialCell */ String, /* colspan */ Double, /* rowspan */ Double) => Unit
    ): Self = StObject.set(x, "onmerge", js.Any.fromFunction4(value))
    
    @scala.inline
    def setOnmergeUndefined: Self = StObject.set(x, "onmerge", js.undefined)
    
    @scala.inline
    def setOnmovecolumn(
      value: (/* element */ JspreadsheetInstanceElement, /* oldPosition */ Double, /* newPosition */ Double) => Unit
    ): Self = StObject.set(x, "onmovecolumn", js.Any.fromFunction3(value))
    
    @scala.inline
    def setOnmovecolumnUndefined: Self = StObject.set(x, "onmovecolumn", js.undefined)
    
    @scala.inline
    def setOnmoverow(
      value: (/* element */ JspreadsheetInstanceElement, /* oldPosition */ String | Double, /* newPosition */ Double) => Unit
    ): Self = StObject.set(x, "onmoverow", js.Any.fromFunction3(value))
    
    @scala.inline
    def setOnmoverowUndefined: Self = StObject.set(x, "onmoverow", js.undefined)
    
    @scala.inline
    def setOnpaste(
      value: (/* element */ JspreadsheetInstanceElement, /* pastedData */ js.Array[js.Array[String]]) => Unit
    ): Self = StObject.set(x, "onpaste", js.Any.fromFunction2(value))
    
    @scala.inline
    def setOnpasteUndefined: Self = StObject.set(x, "onpaste", js.undefined)
    
    @scala.inline
    def setOnredo(
      value: (/* element */ JspreadsheetInstanceElement, /* historyRecord */ js.UndefOr[HistoryRecord]) => Unit
    ): Self = StObject.set(x, "onredo", js.Any.fromFunction2(value))
    
    @scala.inline
    def setOnredoUndefined: Self = StObject.set(x, "onredo", js.undefined)
    
    @scala.inline
    def setOnresizecolumn(
      value: (/* element */ JspreadsheetInstanceElement, /* colIndex */ String | Double, /* newWidth */ String | Double, /* oldWidth */ Double) => Unit
    ): Self = StObject.set(x, "onresizecolumn", js.Any.fromFunction4(value))
    
    @scala.inline
    def setOnresizecolumnUndefined: Self = StObject.set(x, "onresizecolumn", js.undefined)
    
    @scala.inline
    def setOnresizerow(
      value: (/* element */ JspreadsheetInstanceElement, /* rowIndex */ String | Double, /* newHeight */ Double, /* oldHeight */ Double) => Unit
    ): Self = StObject.set(x, "onresizerow", js.Any.fromFunction4(value))
    
    @scala.inline
    def setOnresizerowUndefined: Self = StObject.set(x, "onresizerow", js.undefined)
    
    @scala.inline
    def setOnsave(
      value: (/* element */ JspreadsheetInstanceElement, /* jssInstance */ JspreadsheetInstance, /* data */ Any) => Unit
    ): Self = StObject.set(x, "onsave", js.Any.fromFunction3(value))
    
    @scala.inline
    def setOnsaveUndefined: Self = StObject.set(x, "onsave", js.undefined)
    
    @scala.inline
    def setOnselection(
      value: (/* element */ JspreadsheetInstanceElement, /* borderLeftIndex */ Double, /* borderTopIndex */ Double, /* borderRightIndex */ Double, /* borderBottomIndex */ Double, /* origin */ Any) => Unit
    ): Self = StObject.set(x, "onselection", js.Any.fromFunction6(value))
    
    @scala.inline
    def setOnselectionUndefined: Self = StObject.set(x, "onselection", js.undefined)
    
    @scala.inline
    def setOnsort(
      value: (/* element */ JspreadsheetInstanceElement, /* colIndex */ String | Double, /* order */ `0` | `1`) => Unit
    ): Self = StObject.set(x, "onsort", js.Any.fromFunction3(value))
    
    @scala.inline
    def setOnsortUndefined: Self = StObject.set(x, "onsort", js.undefined)
    
    @scala.inline
    def setOnundo(
      value: (/* element */ JspreadsheetInstanceElement, /* historyRecord */ js.UndefOr[HistoryRecord]) => Unit
    ): Self = StObject.set(x, "onundo", js.Any.fromFunction2(value))
    
    @scala.inline
    def setOnundoUndefined: Self = StObject.set(x, "onundo", js.undefined)
    
    @scala.inline
    def setPagination(value: Double): Self = StObject.set(x, "pagination", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setPaginationOptions(value: js.Array[Double]): Self = StObject.set(x, "paginationOptions", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setPaginationOptionsUndefined: Self = StObject.set(x, "paginationOptions", js.undefined)
    
    @scala.inline
    def setPaginationOptionsVarargs(value: Double*): Self = StObject.set(x, "paginationOptions", js.Array(value :_*))
    
    @scala.inline
    def setPaginationUndefined: Self = StObject.set(x, "pagination", js.undefined)
    
    @scala.inline
    def setParseFormulas(value: Boolean): Self = StObject.set(x, "parseFormulas", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setParseFormulasUndefined: Self = StObject.set(x, "parseFormulas", js.undefined)
    
    @scala.inline
    def setParseTableAutoCellType(value: Boolean): Self = StObject.set(x, "parseTableAutoCellType", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setParseTableAutoCellTypeUndefined: Self = StObject.set(x, "parseTableAutoCellType", js.undefined)
    
    @scala.inline
    def setParseTableFirstRowAsHeader(value: Boolean): Self = StObject.set(x, "parseTableFirstRowAsHeader", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setParseTableFirstRowAsHeaderUndefined: Self = StObject.set(x, "parseTableFirstRowAsHeader", js.undefined)
    
    @scala.inline
    def setPersistance(value: Boolean | String): Self = StObject.set(x, "persistance", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setPersistanceUndefined: Self = StObject.set(x, "persistance", js.undefined)
    
    @scala.inline
    def setRequestVariables(value: Any): Self = StObject.set(x, "requestVariables", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setRequestVariablesUndefined: Self = StObject.set(x, "requestVariables", js.undefined)
    
    @scala.inline
    def setRoot(value: HTMLElement): Self = StObject.set(x, "root", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setRootUndefined: Self = StObject.set(x, "root", js.undefined)
    
    @scala.inline
    def setRowDrag(value: Boolean): Self = StObject.set(x, "rowDrag", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setRowDragUndefined: Self = StObject.set(x, "rowDrag", js.undefined)
    
    @scala.inline
    def setRowResize(value: Boolean): Self = StObject.set(x, "rowResize", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setRowResizeUndefined: Self = StObject.set(x, "rowResize", js.undefined)
    
    @scala.inline
    def setRows(value: js.Array[Row] | (Record[Double, Row])): Self = StObject.set(x, "rows", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setRowsUndefined: Self = StObject.set(x, "rows", js.undefined)
    
    @scala.inline
    def setRowsVarargs(value: Row*): Self = StObject.set(x, "rows", js.Array(value :_*))
    
    @scala.inline
    def setSearch(value: Boolean): Self = StObject.set(x, "search", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setSearchUndefined: Self = StObject.set(x, "search", js.undefined)
    
    @scala.inline
    def setSecureFormulas(value: Boolean): Self = StObject.set(x, "secureFormulas", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setSecureFormulasUndefined: Self = StObject.set(x, "secureFormulas", js.undefined)
    
    @scala.inline
    def setSelectionCopy(value: Boolean): Self = StObject.set(x, "selectionCopy", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setSelectionCopyUndefined: Self = StObject.set(x, "selectionCopy", js.undefined)
    
    @scala.inline
    def setSorting(
      value: /* order */ `0` | `1` => js.Function2[/* itemA */ SortingItem, /* itemB */ SortingItem, Double]
    ): Self = StObject.set(x, "sorting", js.Any.fromFunction1(value))
    
    @scala.inline
    def setSortingUndefined: Self = StObject.set(x, "sorting", js.undefined)
    
    @scala.inline
    def setStripHTML(value: Boolean): Self = StObject.set(x, "stripHTML", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setStripHTMLOnCopy(value: Boolean): Self = StObject.set(x, "stripHTMLOnCopy", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setStripHTMLOnCopyUndefined: Self = StObject.set(x, "stripHTMLOnCopy", js.undefined)
    
    @scala.inline
    def setStripHTMLUndefined: Self = StObject.set(x, "stripHTML", js.undefined)
    
    @scala.inline
    def setStyle(value: Record[String, String]): Self = StObject.set(x, "style", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setStyleUndefined: Self = StObject.set(x, "style", js.undefined)
    
    @scala.inline
    def setTableHeight(value: String): Self = StObject.set(x, "tableHeight", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setTableHeightUndefined: Self = StObject.set(x, "tableHeight", js.undefined)
    
    @scala.inline
    def setTableOverflow(value: Boolean): Self = StObject.set(x, "tableOverflow", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setTableOverflowUndefined: Self = StObject.set(x, "tableOverflow", js.undefined)
    
    @scala.inline
    def setTableWidth(value: String): Self = StObject.set(x, "tableWidth", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setTableWidthUndefined: Self = StObject.set(x, "tableWidth", js.undefined)
    
    @scala.inline
    def setText(value: TranslationOptions): Self = StObject.set(x, "text", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setTextOverflow(value: Boolean): Self = StObject.set(x, "textOverflow", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setTextOverflowUndefined: Self = StObject.set(x, "textOverflow", js.undefined)
    
    @scala.inline
    def setTextUndefined: Self = StObject.set(x, "text", js.undefined)
    
    @scala.inline
    def setToolbar(value: js.Array[ToolbarItem]): Self = StObject.set(x, "toolbar", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setToolbarUndefined: Self = StObject.set(x, "toolbar", js.undefined)
    
    @scala.inline
    def setToolbarVarargs(value: ToolbarItem*): Self = StObject.set(x, "toolbar", js.Array(value :_*))
    
    @scala.inline
    def setUpdateTable(
      value: (/* instance */ JspreadsheetInstance, /* cell */ HTMLTableCellElement, /* colIndex */ Double, /* rowIndex */ Double, /* value */ CellValue, /* displayedValue */ String, /* cellName */ String) => Unit
    ): Self = StObject.set(x, "updateTable", js.Any.fromFunction7(value))
    
    @scala.inline
    def setUpdateTableUndefined: Self = StObject.set(x, "updateTable", js.undefined)
    
    @scala.inline
    def setUrl(value: String): Self = StObject.set(x, "url", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setUrlUndefined: Self = StObject.set(x, "url", js.undefined)
    
    @scala.inline
    def setWordWrap(value: Boolean): Self = StObject.set(x, "wordWrap", value.asInstanceOf[js.Any])
    
    @scala.inline
    def setWordWrapUndefined: Self = StObject.set(x, "wordWrap", js.undefined)
  }
}
